import React, { Fragment } from "react";
import { Books, Resize } from "../../models";
import { eventEmitter, navigate } from "../../services";
import {
    docShareUrl,
    fixArabicText,
    getMetaValue,
    isLoggedIn,
    pageUrl,
    setWindowTitle,
    shareOnFacebook,
} from "../../helpers";
import { downloadPDF } from "../../helpers/pdf";
import { Content } from "../../pages/Template";
import TableOfContents, { Page } from "./TableOfContents";
import { EVT_KEYDOWN, KEY_ESC, TYPE_WHATSAPP_SHARE } from "../../lib";
import { Button } from "../../lib/form";
import {
    ActionImg,
    ActionRow,
    Actions,
    Authors,
    Container,
    Image,
    ImageCol,
    Intro,
    Meta,
    Title,
    TOCColumn,
} from "./styles";
import { download } from "../../images";
import { CircularLoader } from "../../lib/ui/Progress";
import { getURLParam, RouteMatch } from "../../lib/routing";
import OnBoarding from "./OnBoarding";
import { connect } from "react-redux";
import { AppState } from "../../store";
import { AxiosResponse } from "axios";
import { DocMeta } from "../../helpers/meta";

const mapState = ({ user }: AppState) => ({ user });
type StateProps = ReturnType<typeof mapState>;
type Props = StateProps & {
    match: RouteMatch;
};

type Doc = {
    id: number;
    name: string;
    skip_pdf: boolean;
    doc_meta: DocMeta[];
    pages: Page[];
};
type State = {
    doc: Doc | null;
    processingPDF: boolean;
};

class BookPage extends React.Component<Props, State> {
    static paths = ["/app/b-:doc_id-:doc_title", "/app/b-:doc_id-/"];

    constructor(props: Props) {
        super(props);
        this.state = {
            doc: null,
            processingPDF: false,
        };
    }

    async componentDidMount() {
        await this.fetch();
        window.addEventListener(EVT_KEYDOWN, this.onShortcut);
        eventEmitter.on("pdf.processing", this.onPDFProcessing);
    }

    componentWillUnmount() {
        window.removeEventListener(EVT_KEYDOWN, this.onShortcut);
        eventEmitter.off("pdf.processing", this.onPDFProcessing);
    }

    onPDFProcessing = (processingPDF: boolean) =>
        this.setState({ processingPDF });

    onShortcut = (evt: KeyboardEvent) => {
        if (evt.key === KEY_ESC) {
            navigate.back();
        }
    };

    async fetch() {
        try {
            const { doc_id } = this.props.match.params;
            const { data: doc }: AxiosResponse<Doc> = await Books.fetch(doc_id);

            doc.pages = doc.pages.map((p: Page) => {
                p.title = p.name;
                p.link = pageUrl(doc, p);

                return p;
            });

            setWindowTitle(doc.name);

            this.setState({ doc });
        } catch (e) {}
    }

    onReadBook = () => {
        if (this.state.doc) {
            navigate.to(this.state.doc.pages[0].link);
        }
    };

    onShare = () => {
        const doc = this.state.doc!;

        shareOnFacebook(docShareUrl(doc!));

        const { gtag } = window;
        gtag("event", "share", {
            event_category: "book",
            event_label: `facebook-${doc!.id}`,
        });
    };

    onDownload = async () => {
        await downloadPDF(this.state.doc!.id);
    };

    downloadBtn() {
        const { doc, processingPDF } = this.state;
        if (!doc || doc!.skip_pdf) {
            return null;
        }

        return (
            <Fragment>
                {processingPDF && (
                    <CircularLoader
                        margin="6px 12px 0 0"
                        border="2px"
                        width="24px"
                        height="24px"
                    />
                )}

                {!processingPDF && (
                    <ActionImg src={download} onClick={this.onDownload} />
                )}
            </Fragment>
        );
    }

    cover() {
        const coverImg = Resize.url(
            "GET",
            `book/cover/${this.state.doc!.id}/440`
        );
        return <Image src={coverImg} onClick={this.onReadBook} />;
    }

    meta() {
        if (!this.state.doc) {
            return null;
        }

        const { name, doc_meta } = this.state.doc;
        const authors = getMetaValue(doc_meta, "authors");
        const intro = getMetaValue(doc_meta, "intro")!;

        return (
            <Meta>
                <Title
                    dangerouslySetInnerHTML={fixArabicText(name)}
                    onClick={this.onReadBook}
                />
                <Authors>{authors}</Authors>

                {this.actions()}

                <Intro dangerouslySetInnerHTML={fixArabicText(intro)} />
            </Meta>
        );
    }

    toc() {
        return (
            <TOCColumn>
                <TableOfContents pages={this.state.doc!.pages} />
            </TOCColumn>
        );
    }

    actions() {
        return (
            <Actions>
                <ActionRow>
                    <Button
                        type={TYPE_WHATSAPP_SHARE}
                        width="230px"
                        margin="0 0 0 10px"
                        messageId="book.shareWithFriends"
                        onClick={this.onShare}
                    />
                </ActionRow>
                <ActionRow>
                    <Button
                        width="150px"
                        margin="0 0 0 10px"
                        messageId="book.read"
                        onClick={this.onReadBook}
                    />

                    {this.downloadBtn()}
                </ActionRow>
            </Actions>
        );
    }

    render() {
        const { doc } = this.state;
        if (!doc) {
            return null;
        }

        if (!isLoggedIn(this.props.user) && doc.id === 170) {
            return (
                <Content isLandingPage={true}>
                    <OnBoarding />
                </Content>
            );
        }

        const isLandingPage = !!getURLParam("fbclid");

        return (
            <Content isLandingPage={isLandingPage}>
                <Container>
                    <ImageCol>
                        {this.cover()}
                        {this.meta()}
                    </ImageCol>
                    {this.toc()}
                </Container>
            </Content>
        );
    }
}

export default connect(mapState)(BookPage);
